/**
 * FleetImportTable — Editable table for reviewing extracted fleet data.
 *
 * Props:
 *   vehicles: array of extracted vehicles
 *   vehicleSpecs: { battery_capacity_kwh, efficiency_kwh_per_mile, ... }
 *   onVehicleUpdate: (index, updatedVehicle) => void
 *   tableRef: ref for scroll-to-row behavior
 */
import React from 'react';
import {
    Box, Typography, TextField, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { vortxColors, useThemeMode } from '@vortx/shared-ui';

function FleetImportTable({
    vehicles = [],
    vehicleSpecs = {},
    onVehicleUpdate,
    tableRef,
}) {
    const { isRetro } = useThemeMode();
    const efficiency = vehicleSpecs.efficiency_kwh_per_mile || 0;

    const handleFieldChange = (index, field, value) => {
        const updated = { ...vehicles[index], [field]: value };

        // Recalculate energy if miles changed
        if (field === 'daily_miles' && efficiency > 0) {
            updated.energy_kwh = Math.round(value * efficiency * 10) / 10;
        }

        onVehicleUpdate(index, updated);
    };

    const handleAvailabilityChange = (vehicleIndex, windowIndex, field, value) => {
        const vehicle = { ...vehicles[vehicleIndex] };
        const avail = [...(vehicle.depot_availability || [])];
        avail[windowIndex] = { ...avail[windowIndex], [field]: value };

        // Clear MISSING flag if user fills in arrival
        if (field === 'available_from' && value && value !== 'MISSING') {
            vehicle.flags = (vehicle.flags || []).filter(
                (f) => !f.toLowerCase().includes('missing arrival')
            );
        }

        vehicle.depot_availability = avail;
        onVehicleUpdate(vehicleIndex, vehicle);
    };

    return (
        <TableContainer
            ref={tableRef}
            sx={{ maxHeight: 480, overflowY: 'auto', overflowX: 'auto' }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 40, bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}` }} />
                        <TableCell sx={{ bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}`, color: isRetro ? vortxColors.white : vortxColors.charcoal }}>
                            Route
                        </TableCell>
                        <TableCell sx={{ bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}`, color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 80 }}>
                            Miles
                        </TableCell>
                        <TableCell sx={{ bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}`, color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 120 }}>
                            Arrival
                        </TableCell>
                        <TableCell sx={{ bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}`, color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 120 }}>
                            Departure
                        </TableCell>
                        <TableCell sx={{ bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}`, color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 90 }}>
                            Energy
                        </TableCell>
                        <TableCell sx={{ bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}`, color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 80 }}>
                            SOC
                        </TableCell>
                        <TableCell sx={{ bgcolor: isRetro ? '#1a1a1a' : '#f5f5f5', borderBottom: `2px solid ${vortxColors.gold}`, color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 80 }}>
                            Charge
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {vehicles.map((vehicle, i) => {
                        const hasMissing = vehicle.depot_availability?.some(
                            (w) => w.available_from === 'MISSING'
                        );
                        const firstWindow = vehicle.depot_availability?.[0] || {};

                        return (
                            <TableRow
                                key={i}
                                id={`fleet-import-row-${i}`}
                                sx={{
                                    '&:hover': { bgcolor: 'rgba(178,140,80,0.08)' },
                                    bgcolor: hasMissing ? 'rgba(237,108,2,0.06)' : 'transparent',
                                }}
                            >
                                <TableCell>
                                    {hasMissing ? (
                                        <Tooltip title="Missing arrival time — fill in below">
                                            <WarningIcon sx={{ color: '#ed6c02', fontSize: 18 }} />
                                        </Tooltip>
                                    ) : (
                                        <CheckCircleIcon sx={{ color: vortxColors.gold, fontSize: 18 }} />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: vortxColors.white, fontWeight: 500 }}>
                                        {vehicle.route_name || vehicle.route_id}
                                    </Typography>
                                    {vehicle.route_id && vehicle.route_name && (
                                        <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                                            Route {vehicle.route_id}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        type="number"
                                        value={vehicle.daily_miles || ''}
                                        onChange={(e) => handleFieldChange(i, 'daily_miles', Number(e.target.value))}
                                        size="small"
                                        variant="standard"
                                        inputProps={{ min: 0, max: 999, style: { color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 60 } }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        type="time"
                                        value={firstWindow.available_from === 'MISSING' ? '' : (firstWindow.available_from || '')}
                                        onChange={(e) => handleAvailabilityChange(i, 0, 'available_from', e.target.value)}
                                        size="small"
                                        variant="standard"
                                        placeholder="Required"
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ style: { color: hasMissing ? '#ed6c02' : vortxColors.white, width: 90 } }}
                                        error={hasMissing}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        type="time"
                                        value={firstWindow.available_until || ''}
                                        onChange={(e) => handleAvailabilityChange(i, 0, 'available_until', e.target.value)}
                                        size="small"
                                        variant="standard"
                                        inputProps={{ style: { color: isRetro ? vortxColors.white : vortxColors.charcoal, width: 90 } }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                        {vehicle.energy_kwh != null ? `${vehicle.energy_kwh} kWh` : '\u2014'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                        {vehicle.arrival_soc != null ? `${Math.round(vehicle.arrival_soc * 100)}%` : '\u2014'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                                        {vehicle.est_charge_hours != null ? `${vehicle.est_charge_hours}h` : '\u2014'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default FleetImportTable;
